export const TEMPLATES = {
  INFOGRAPHIC: "infographic",
  MODERN: "modern",
  CLASSIC: "classic",
  MINIMAL: "minimal",
} as const;

export type TemplateType = typeof TEMPLATES[keyof typeof TEMPLATES];

export interface TemplateConfig {
  id: TemplateType;
  name: string;
  description: string;
  thumbnail: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: string;
    muted: string;
    dark: string;
  };
  typography: {
    headingFont: string;
    bodyFont: string;
    headingWeight: string;
  };
  borderRadius: {
    small: string;
    medium: string;
    large: string;
    section: string;
  };
  shadows: {
    card: string;
    elevated: string;
  };
  defaultSections: string[];
}

export const TEMPLATE_CONFIGS: Record<TemplateType, TemplateConfig> = {
  // The infographic template is brand-driven: these colours are only the
  // neutral platform fallback used when an environment has configured none.
  // At render time every value is replaced by the tenant's own configuration.
  [TEMPLATES.INFOGRAPHIC]: {
    id: TEMPLATES.INFOGRAPHIC,
    name: "Infographic",
    description: "Modern, visual sales proposal — cards, metrics, timeline and pricing blocks",
    thumbnail: "/templates/infographic-thumb.png",
    colors: {
      primary: "#0058C3",
      secondary: "#14161A",
      accent: "#0070F3",
      background: "#F6F7F9",
      surface: "#FFFFFF",
      text: "#1B1B1D",
      muted: "#767A82",
      dark: "#14161A",
    },
    typography: {
      headingFont: "'Inter', system-ui, sans-serif",
      bodyFont: "'Inter', system-ui, sans-serif",
      headingWeight: "800",
    },
    borderRadius: { small: "8px", medium: "12px", large: "14px", section: "16px" },
    shadows: {
      card: "0 1px 2px rgba(20,20,24,0.06)",
      elevated: "0 12px 40px rgba(20,20,24,0.12)",
    },
    // Sections come from the proposal-type preset in config/infographicTemplate.
    defaultSections: [
      "cover",
      "client-info",
      "executive-summary",
      "understanding",
      "proposed-solution",
      "scope-of-work",
      "deliverables",
      "approach",
      "timeline",
      "pricing",
      "why-us",
      "terms",
      "next-steps",
      "contact",
    ],
  },

  [TEMPLATES.MODERN]: {
    id: TEMPLATES.MODERN,
    name: "Modern Luxury",
    description: "Navy & gold luxury design with elegant typography",
    thumbnail: "/templates/modern-thumb.png",
    colors: {
      primary: "#C9A84C",
      secondary: "#0B1D3A",
      accent: "#E8C96A",
      background: "#FAF6ED",
      surface: "#FFFFFF",
      text: "#0B1D3A",
      muted: "#6B82A8",
      dark: "#0B1D3A",
    },

    typography: {
      headingFont: "'Playfair Display', serif",
      bodyFont: "'DM Sans', sans-serif",
      headingWeight: "900",
    },
    borderRadius: {
      small: "8px",
      medium: "12px",
      large: "16px",
      section: "24px",
    },
    shadows: {
      card: "0 1px 3px rgba(0,0,0,0.08)",
      elevated: "0 8px 30px rgba(0,0,0,0.12)",
    },

    defaultSections: [
      "overview",
      "objectives",
      "challenges",
      "solution",
      "process",
      "offer",
      "testimonials",
      "recent-work",
      "cta",
      "footer",
    ],
  },

  [TEMPLATES.CLASSIC]: {
    id: TEMPLATES.CLASSIC,
    name: "Classic Green",
    description: "Clean Upwork-inspired design with green accents",
    thumbnail: "/templates/classic-thumb.png",
    colors: {
      primary: "#14A800",
      secondary: "#001E00",
      accent: "#22c55e",
      background: "#F5F7F6",
      surface: "#FFFFFF",
      text: "#001E00",
      muted: "#6B7280",
      dark: "#052E16",
    },
    typography: {
      headingFont: "'Plus Jakarta Sans', sans-serif",
      bodyFont: "'Plus Jakarta Sans', sans-serif",
      headingWeight: "800",
    },
    borderRadius: {
      small: "6px",
      medium: "10px",
      large: "14px",
      section: "20px",
    },
    shadows: {
      card: "0 2px 8px rgba(0,0,0,0.06)",
      elevated: "0 12px 40px rgba(0,0,0,0.1)",
    },
    defaultSections: [
      "overview",
      "objectives",
      "solution",
      "process",
      "offer",
      "testimonials",
      "cta",
      "footer",
    ],
  },

  [TEMPLATES.MINIMAL]: {
    id: TEMPLATES.MINIMAL,
    name: "Minimal Clean",
    description: "Minimalist professional design with clean lines",
    thumbnail: "/templates/minimal-thumb.png",
    colors: {
      primary: "#14A800",
      secondary: "#001E00",
      accent: "#22c55e",
      background: "#FFFFFF",
      surface: "#FFFFFF",
      text: "#001E00",
      muted: "#71717A",
      dark: "#001E00",
    },
    typography: {
      headingFont: "'Public Sans', sans-serif",
      bodyFont: "'Public Sans', sans-serif",
      headingWeight: "700",
    },
    borderRadius: {
      small: "4px",
      medium: "8px",
      large: "12px",
      section: "16px",
    },
    shadows: {
      card: "0 1px 2px rgba(0,0,0,0.04)",
      elevated: "0 4px 20px rgba(0,0,0,0.08)",
    },
    defaultSections: [
      "overview",
      "objectives",
      "solution",
      "offer",
      "cta",
      "footer",
    ],
  },
};

export const SECTION_TYPES = {
  SIMPLE_TEXT: "simple-text",
  FEATURE_GRID: "feature-grid",
  DARK_GRID: "dark-grid",
  TEXT_WITH_POINTS: "text-with-points",
  STEPS: "steps",
  TESTIMONIAL_GRID: "testimonial-grid",
  PORTFOLIO_GRID: "portfolio-grid",
  WEBSITE_SHOWCASE: "website-showcase",
  CALL_TO_ACTION: "call-to-action",
  FOOTER: "footer",
  HERO: "hero",
  STATS: "stats",
} as const;

export type SectionType = typeof SECTION_TYPES[keyof typeof SECTION_TYPES];

export interface AvailableSection {
  id: string;
  type: SectionType;
  title: string;
  description: string;
  icon: string;
  defaultContent: any;
  layouts: string[];
}

export const AVAILABLE_SECTIONS: AvailableSection[] = [
  {
    id: "overview",
    type: SECTION_TYPES.SIMPLE_TEXT,
    title: "Project Overview",
    description: "Introduction and project summary",
    icon: "Info",
    defaultContent: { text: "Add your project overview here..." },
    layouts: ["card", "full-width", "bordered"],
  },
  {
    id: "website-showcase",
    type: SECTION_TYPES.WEBSITE_SHOWCASE,
    title: "Website Showcase",
    description: "Link live websites as preview cards",
    icon: "Globe",
    defaultContent: { items: [] },
    layouts: ["grid-2", "grid-3"],
  },
  {
    id: "objectives",
    type: SECTION_TYPES.FEATURE_GRID,
    title: "Your Objectives",
    description: "Client goals in a grid format",
    icon: "Target",
    defaultContent: {
      items: [
        { title: "Objective 1", description: "Description here", icon: "Check" },
        { title: "Objective 2", description: "Description here", icon: "Check" },
      ],
    },
    layouts: ["grid-2", "grid-3", "grid-4"],
  },
  {
    id: "challenges",
    type: SECTION_TYPES.DARK_GRID,
    title: "Key Challenges",
    description: "Problems to solve in dark theme",
    icon: "AlertTriangle",
    defaultContent: {
      items: [
        { title: "Challenge 1", description: "Description here" },
        { title: "Challenge 2", description: "Description here" },
        { title: "Challenge 3", description: "Description here" },
      ],
    },
    layouts: ["grid-3", "grid-2"],
  },
  {
    id: "solution",
    type: SECTION_TYPES.TEXT_WITH_POINTS,
    title: "Our Solution",
    description: "Solution with bullet points",
    icon: "Lightbulb",
    defaultContent: {
      text: "Our creative approach...",
      points: ["Point 1", "Point 2", "Point 3"],
      highlight: "Key benefit highlighted here",
    },
    layouts: ["split", "stacked"],
  },
  {
    id: "process",
    type: SECTION_TYPES.STEPS,
    title: "Our Approach",
    description: "Step-by-step process",
    icon: "Route",
    defaultContent: {
      steps: [
        { title: "Discovery", description: "Understanding your needs" },
        { title: "Design", description: "Creating the solution" },
        { title: "Delivery", description: "Implementing and launching" },
      ],
    },
    layouts: ["grid-3", "vertical", "timeline"],
  },
  {
    id: "offer",
    type: SECTION_TYPES.FEATURE_GRID,
    title: "Our Offer",
    description: "What's included",
    icon: "FileCheck",
    defaultContent: {
      items: [
        { title: "Deliverable 1", description: "Description" },
        { title: "Deliverable 2", description: "Description" },
        { title: "Deliverable 3", description: "Description" },
      ],
    },
    layouts: ["grid-3", "grid-2", "list"],
  },
  {
    id: "testimonials",
    type: SECTION_TYPES.TESTIMONIAL_GRID,
    title: "Testimonials",
    description: "Client reviews and feedback",
    icon: "Star",
    defaultContent: {
      items: [
        { quote: "Amazing work!", name: "John Doe", position: "CEO, Company" },
        { quote: "Highly recommend!", name: "Jane Smith", position: "Director" },
      ],
    },
    layouts: ["grid-3", "grid-2", "carousel"],
  },
  {
    id: "recent-work",
    type: SECTION_TYPES.PORTFOLIO_GRID,
    title: "Recent Work",
    description: "Portfolio showcase",
    icon: "Briefcase",
    defaultContent: {
      subtitle: "Selected projects",
      // Filled from the Portfolio Library when the section is added.
      projects: [],
    },
    layouts: ["grid-3", "grid-2", "masonry"],
  },
  {
    id: "cta",
    type: SECTION_TYPES.CALL_TO_ACTION,
    title: "Call to Action",
    description: "Hire me section",
    icon: "Handshake",
    defaultContent: {
      text: "Ready to get started? Let's build something amazing together.",
      buttonText: "Hire Me Now",
    },
    layouts: ["centered", "split"],
  },
  {
    id: "footer",
    type: SECTION_TYPES.FOOTER,
    title: "Footer",
    description: "Proposal footer",
    icon: "FileText",
    defaultContent: {
      text: "Powered by e360Digital • Professional Proposal Framework",
    },
    layouts: ["simple", "detailed"],
  },
  {
    id: "stats",
    type: SECTION_TYPES.STATS,
    title: "Statistics",
    description: "Key metrics and numbers",
    icon: "BarChart",
    defaultContent: {
      items: [
        { value: "100%", label: "Job Success" },
        { value: "50+", label: "Projects Completed" },
        { value: "5★", label: "Client Rating" },
      ],
    },
    layouts: ["horizontal", "grid-4"],
  },
];

export const getSectionConfig = (id: string): AvailableSection | undefined => {
  return AVAILABLE_SECTIONS.find((s) => s.id === id);
};

export const getSectionByType = (type: SectionType): AvailableSection | undefined => {
  return AVAILABLE_SECTIONS.find((s) => s.type === type);
};
